import type { FC, ErrorInfo } from 'react';
import React, { useState, useCallback, useEffect } from 'react';

interface Props {
    error: Error | null;
    errorInfo: ErrorInfo | null;
    /**
     * When provided, a "Reset ErrorBoundary" button is rendered that invokes
     * this callback. Omit in contexts where there is no boundary to reset
     * (e.g. server-side rendered error pages).
     */
    onReset?: () => void;
    /** Override for the main heading. Defaults to the React ErrorBoundary copy. */
    title?: string;
    /** Optional contextual line rendered under the subtitle (e.g. "GET /foo"). */
    meta?: string;
}

interface ActionButtonProps {
    onClick: () => void;
    label: string;
    'data-dev-error-reload'?: boolean;
}

/** Plain action button (reload / reset) sharing `CopyButton`'s visual style. */
const ActionButton: FC<ActionButtonProps> = ({ onClick, label, ...rest }) => (
    <button type="button" onClick={onClick} style={styles.copyButton} {...rest}>
        <span>{label}</span>
    </button>
);

interface CopyButtonProps {
    value: string;
    label?: string;
}

const CopyButton: FC<CopyButtonProps> = ({ value, label = 'Copy' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.warn('[DevErrorPage] Clipboard copy failed', err);
            setCopied(false);
        }
    }, [value]);

    return (
        <button
            type="button"
            onClick={handleCopy}
            style={styles.copyButton}
            aria-label={copied ? 'Copied' : label}
            title={copied ? 'Copied!' : label}
        >
            <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                {copied ? (
                    <polyline points="20 6 9 17 4 12" />
                ) : (
                    <>
                        <rect
                            x="9"
                            y="9"
                            width="13"
                            height="13"
                            rx="2"
                            ry="2"
                        />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </>
                )}
            </svg>
            <span>{copied ? 'Copied' : label}</span>
        </button>
    );
};

/**
 * Dev-only error page. Renders without styled-components so it still
 * displays even if the styled-components runtime is the thing that blew up.
 * Shows the error message, full stack, and the React component stack, plus
 * buttons to reset the boundary or reload the page.
 *
 * Shared between:
 *  - the client React ErrorBoundary in `themes/archipro2` (old Business
 *    Manager stack)
 *  - the Express error handler in `packages/web/server` (new stack),
 *    which server-renders this component via `renderDevErrorPageHtml`.
 */
const DevErrorPage: FC<Props> = ({
    error,
    errorInfo,
    onReset,
    title,
    meta,
}) => {
    const message = error?.message ?? 'Unknown error';

    useEffect(() => {
        document.title = 'Dev error: ' + message;
    }, [message]);

    const stack = error?.stack ?? '(no stack available)';
    const componentStack = errorInfo?.componentStack ?? null;
    const heading =
        title ?? 'SWW: A React error was caught by the ErrorBoundary';

    // A "useXxx must be used within a XxxProvider" error after an HMR update
    // is almost always a duplicated React Context instance caused by a module
    // cycle being re-evaluated. A full page reload re-mounts provider and
    // consumer against the same context instance and clears it.
    const likelyStaleContext = /must be used within.*Provider/i.test(message);

    return (
        <div style={styles.page}>
            <div style={styles.inner}>
                <div style={styles.badge}>DEV ONLY</div>
                <h1 style={styles.title}>{heading}</h1>
                <p style={styles.subtitle}>
                    This detailed view is only rendered when{' '}
                    <code style={styles.code}>
                        process.env.NODE_ENV !== 'production'
                    </code>
                    . In production the generic "Something went wrong" page is
                    shown instead.
                </p>
                {meta && <p style={styles.meta}>{meta}</p>}

                {likelyStaleContext && (
                    <div style={styles.staleCtxCallout}>
                        <strong>Looks like a stale React context.</strong> This
                        usually happens when a module was hot-reloaded inside a
                        circular import and its{' '}
                        <code style={styles.code}>createContext()</code> call
                        produced a new instance. A full page reload
                        (Cmd&#8209;Shift&#8209;R) will re-mount the providers
                        and clear it. If it happens repeatedly on one file, you
                        likely have a runtime import cycle to break.
                    </div>
                )}

                <section style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={{ ...styles.sectionTitle, margin: 0 }}>
                            Message
                        </h2>
                        <div style={styles.sectionActions}>
                            <ActionButton
                                label="Reload page"
                                onClick={() => window.location.reload()}
                                data-dev-error-reload
                            />
                            <CopyButton value={message} label="Copy message" />
                        </div>
                    </div>
                    <pre style={styles.pre}>{message}</pre>
                </section>

                <section style={styles.section}>
                    <div style={styles.sectionHeader}>
                        <h2 style={{ ...styles.sectionTitle, margin: 0 }}>
                            Stack trace
                        </h2>
                        <div style={styles.sectionActions}>
                            {onReset && (
                                <ActionButton
                                    label="Reset ErrorBoundary"
                                    onClick={onReset}
                                />
                            )}
                            <CopyButton value={stack} label="Copy stack" />
                        </div>
                    </div>
                    <pre style={styles.pre}>{stack}</pre>
                </section>

                {componentStack && (
                    <section style={styles.section}>
                        <div style={styles.sectionHeader}>
                            <h2 style={{ ...styles.sectionTitle, margin: 0 }}>
                                React component stack
                            </h2>
                            <CopyButton
                                value={componentStack}
                                label="Copy component stack"
                            />
                        </div>
                        <pre style={styles.pre}>{componentStack}</pre>
                    </section>
                )}

                <p style={styles.hint}>
                    Tip: also open the browser DevTools console — the full error
                    object and{' '}
                    <code style={styles.code}>componentDidCatch</code> output is
                    logged there.
                </p>
            </div>
        </div>
    );
};

const styles: Record<string, React.CSSProperties> = {
    // `position: fixed` + a z-index above every app z-index token (Rocco's
    // scale tops out at `counter: 180`) makes this a true full-viewport
    // takeover regardless of where it's mounted. It's not always a standalone
    // document — CatchPage renders ErrorPage "between top nav and footer"
    // inside AppLayout for non-404/403/401/410 statuses, so a normal-flow
    // `min-height: 100vh` block here would sit *below* the site's fixed
    // header and get visually cut through by it instead of covering it.
    page: {
        position: 'fixed',
        inset: 0,
        zIndex: 999999,
        overflow: 'auto',
        padding: '32px 24px',
        background: '#1f1f23',
        color: '#f5f5f5',
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: 13,
        lineHeight: 1.5,
        boxSizing: 'border-box',
    },
    inner: {
        maxWidth: 960,
        margin: '0 auto',
    },
    badge: {
        display: 'inline-block',
        background: '#f59e0b',
        color: '#1f1f23',
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: 0.5,
        padding: '2px 8px',
        borderRadius: 4,
        marginBottom: 12,
    },
    title: {
        fontSize: 22,
        fontWeight: 600,
        margin: '0 0 8px',
        color: '#ff8080',
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    subtitle: {
        margin: '0 0 24px',
        color: '#c8c8d0',
        fontSize: 12,
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    meta: {
        margin: '-16px 0 24px',
        color: '#9aa0a6',
        fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        fontSize: 12,
    },
    staleCtxCallout: {
        margin: '0 0 24px',
        padding: '12px 14px',
        background: '#3b2e0f',
        border: '1px solid #f59e0b',
        borderRadius: 6,
        color: '#fbe7b7',
        lineHeight: 1.6,
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    section: {
        marginBottom: 20,
    },
    sectionHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        margin: '0 0 6px',
    },
    sectionTitle: {
        fontSize: 13,
        textTransform: 'uppercase',
        letterSpacing: 1,
        color: '#9aa0a6',
        margin: '0 0 6px',
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    sectionActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    pre: {
        background: '#101014',
        border: '1px solid #333',
        borderRadius: 6,
        padding: 12,
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowX: 'auto',
        color: '#f5f5f5',
    },
    code: {
        background: '#101014',
        padding: '1px 6px',
        borderRadius: 3,
    },
    hint: {
        marginTop: 24,
        color: '#9aa0a6',
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    copyButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        background: '#2d2d33',
        color: '#f5f5f5',
        border: '1px solid #444',
        borderRadius: 4,
        cursor: 'pointer',
        fontSize: 12,
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        lineHeight: 1,
    },
};

export default DevErrorPage;
